import Spinner from '@src/components/common/Spinner';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import {
  PickerMenu,
  PickerRow,
  PickerRowMeta,
  PickerTrigger,
} from './reviewPaneHeaderPicker';

import type { TranslationViewMode } from './alignedBlocks';

// PLAT-10264 (T5) — the three view modes. All three are always selectable
// (pre-split, Words shows the same blocks as Segment, per the epic doc —
// selectable, not disabled). Title Case labels.
const VIEW_MODE_OPTIONS: {
  id: TranslationViewMode;
  label: string;
  hint: string;
}[] = [
  { id: 'paragraph', label: 'Paragraph', hint: 'One row per paragraph' },
  { id: 'segment', label: 'Segment', hint: 'One row per translated segment' },
  { id: 'fragment', label: 'Words', hint: 'One row per word group' },
];

const SPLIT_ALL_TOOLTIP_ID = 'review-split-all-tooltip';

export interface TranscriptViewControlsProps {
  viewMode: TranslationViewMode;
  onViewModeChange: (next: TranslationViewMode) => void;
  /** The global "Split into Words" action; rendered in the Words view only. */
  splitAll: {
    canSplit: boolean;
    isSplitting: boolean;
    onSplit: () => void;
  };
  /** Sits above the workspace overlay (which varies by entry, PLAT-10286). */
  menuZIndex: number;
}

/**
 * PLAT-11649 — the transcript's own controls, in the transcript's own header:
 * which granularity the aligned rows use (Paragraph / Segment / Words), as a
 * dropdown beside the pane's "Translation · <language>" label, and the "Split
 * into Words" action that belongs to the Words view. They used to sit in the
 * workspace header beside the mode toggle and the review link, where three
 * unrelated segmented controls read as one confusing row.
 */
export const TranscriptViewControls: React.FC<TranscriptViewControlsProps> = ({
  viewMode,
  onViewModeChange,
  splitAll,
  menuZIndex,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const { menuRef, style } = useAnchoredDropdown(open, triggerRef, close, 200);

  const current = VIEW_MODE_OPTIONS.find(o => o.id === viewMode)!;

  return (
    <Controls>
      {viewMode === 'fragment' && (
        <SplitAllButton
          type="button"
          // Design system: an inert control uses `aria-disabled`, NEVER the
          // native attribute — a natively-disabled button fires no events, so
          // its explanatory tooltip never shows and the reviewer is left
          // guessing why it is greyed out.
          aria-disabled={!splitAll.canSplit || splitAll.isSplitting}
          // One indicator per wait: while splitting, the spinner and label
          // ARE the message, so the tooltip stands down and only explains the
          // inert-before-any-manual-fix case.
          aria-busy={splitAll.isSplitting}
          data-tooltip-id={SPLIT_ALL_TOOLTIP_ID}
          data-tooltip-content={
            splitAll.canSplit || splitAll.isSplitting
              ? undefined
              : 'Fix a translation manually first — splitting uses your corrections as the guide'
          }
          onClick={() => {
            // `aria-disabled` does not block clicks; the guard does.
            if (!splitAll.canSplit || splitAll.isSplitting) return;
            splitAll.onSplit();
          }}
        >
          {/* The visible content and a hidden widest-label sizer share one
              grid cell, so swapping "Split into Words" for "Splitting…" cannot
              resize the control. */}
          <SplitAllLabelStack>
            <SplitAllContent>
              {splitAll.isSplitting ? (
                <>
                  <SplitAllSpinner />
                  Splitting…
                </>
              ) : (
                'Split into Words'
              )}
            </SplitAllContent>
            <SplitAllSizer aria-hidden="true">Split into Words</SplitAllSizer>
          </SplitAllLabelStack>
        </SplitAllButton>
      )}
      <PickerTrigger
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Transcript view"
        data-testid="review-view-mode"
        onClick={() => setOpen(o => !o)}
      >
        {current.label}
        <ChevronDownIcon width="12" height="12" strokeColor="var(--accent)" />
      </PickerTrigger>
      {open &&
        style &&
        createPortal(
          <PickerMenu
            ref={menuRef}
            // The anchoring hook pins `width` to the trigger; the rows carry a
            // hint each and must never truncate, so grow to fit content with
            // the trigger width as the floor (as the language picker does).
            style={{
              ...style,
              zIndex: menuZIndex,
              width: 'max-content',
              minWidth: style.width,
            }}
            role="listbox"
            aria-label="Transcript view options"
          >
            {VIEW_MODE_OPTIONS.map(option => (
              <PickerRow
                key={option.id}
                type="button"
                role="option"
                aria-selected={viewMode === option.id}
                $selected={viewMode === option.id}
                data-testid={`review-view-mode-${option.id}`}
                onClick={() => {
                  onViewModeChange(option.id);
                  close();
                }}
              >
                {option.label}
                <PickerRowMeta>{option.hint}</PickerRowMeta>
              </PickerRow>
            ))}
          </PickerMenu>,
          document.body,
        )}
      <Tooltip id={SPLIT_ALL_TOOLTIP_ID} style={MODAL_TOOLTIP_STYLE} />
    </Controls>
  );
};

// Right-aligned in the pane header; the label beside it is what gives way.
const Controls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
`;

// The "Split into Words" action — Ghost treatment (design system tier 3):
// unbordered, muted grey text with a background-shift hover. Sized to the pane
// header's --text-xs chrome, like the picker beside it.
const SplitAllButton = styled.button`
  border: 1px solid transparent;
  border-radius: var(--radius-sm, 6px);
  padding: 2px 8px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  background: none;
  color: var(--text-muted);

  &:hover:not([aria-disabled='true']) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  // Inert, not natively disabled (see the JSX): muted, no hover, default
  // cursor — the tooltip carries the reason.
  &[aria-disabled='true'] {
    color: var(--text-muted);
    opacity: 0.5;
    cursor: default;
  }
`;

// PLAT-11514 — busy-in-place (design system: "a user-triggered action in
// flight, on a surface that stays" → the control collapses to a busy pill,
// label + Spinner). This button stays put in the header, so the wait belongs
// here rather than in a toast.
const SplitAllLabelStack = styled.span`
  display: grid;

  > * {
    grid-area: 1 / 1;
    white-space: nowrap;
  }
`;

const SplitAllContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const SplitAllSizer = styled.span`
  visibility: hidden;
  pointer-events: none;
`;

// The shared Spinner at `sm` is 16px, TALLER than this control's 12px text
// line; `1em` pins it to the text line so the button never grows mid-split.
const SplitAllSpinner = styled(Spinner).attrs({ size: 'sm' as const })`
  width: 1em;
  height: 1em;
  flex-shrink: 0;
`;
